import React from 'react';
import { useNavigate } from 'react-router-dom';
import clinicsImg from '../assets/clinics.webp';
import privateLessonsImg from '../assets/private-lessons.jpg';

interface Lesson {
  title: string;
  summary: string[];
  imageSrc: string;
  imageAlt: string;
  actionOnClickPath: string;
  actionTitle: string;
}

interface LessonsCardsProps {
  showHeader?: boolean;
  lessons?: Lesson[];
}

const defaultLessons: Lesson[] = [
  {
    title: 'Private Lessons',
    summary: [
      'Personalized coaching for all skill levels',
      'Flexible scheduling to suit you',
      'Quickly improve with focused guidance',
    ],
    imageAlt: 'Private Lessons',
    imageSrc: privateLessonsImg,
    actionOnClickPath: '/courses',
    actionTitle: 'Join now',
  },
  {
    title: 'Tennis Clinics',
    summary: [
      'Targeted sessions for specific skills',
      'Master serves, forehands, backhands, and more',
      'Sharpen technique in a supportive setting',
    ],
    imageAlt: 'Tennis Clinics',
    imageSrc: clinicsImg,
    actionOnClickPath: '/courses',
    actionTitle: 'Learn more',
  },
];

const LessonsCards: React.FC<LessonsCardsProps> = ({ showHeader, lessons = defaultLessons }) => {
  const navigate = useNavigate();
  return (
    <section className="py-24 bg-gray-50 flex flex-col items-center">
      {showHeader && (
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold text-navy mb-4">Start Your Tennis Journey</h1>
          <div className="w-24 h-1.5 bg-primary mx-auto rounded-full" />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 w-full place-items-center">
        {lessons.map((lesson, idx) => (
          <div 
            key={lesson.title + idx}
            className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col border border-gray-100 max-w-lg w-full"
          >
            <div className="relative h-80 overflow-hidden">
              <img 
                src={lesson.imageSrc} 
                alt={lesson.imageAlt} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
            <div className="p-10 flex-1 flex flex-col">
              <h2 className="text-3xl font-bold mb-6 text-navy group-hover:text-primary transition-colors">
                {lesson.title}
              </h2>
              <ul className="space-y-4 mb-10 flex-1">
                {lesson.summary.map((el, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 leading-relaxed">
                    <span className="text-primary mt-1">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </span>
                    {el.replace('• ', '')}
                  </li>
                ))}
              </ul>
              <button
                className="btn-premium bg-primary text-white hover:bg-primary-dark w-full"
                onClick={() => {
                  navigate(lesson.actionOnClickPath, { state: { targetId: 'learn-more' } });
                }}
              >
                {lesson.actionTitle}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default LessonsCards;
